/** tm/components/CollectDetailModal.tsx — 자료수집 현황 모달 (DetailScreen 100% 일치) */
import { useState, useEffect, useCallback } from 'react';
import { C, T, R } from './ui';
import * as api from '../api';
import PdfEditor from '../../PdfEditor';
import BundleLinkModal from './BundleLinkModal';

interface Guide { id?: string; type: string; label: string; url?: string; fileName?: string; file_name?: string; filePath?: string; s3Key?: string }
interface Item { id: string; label: string; type: string; required: boolean; sensitive?: boolean; description?: string; sort_order: number; guides?: Guide[]; options?: string[] }
interface Upload { id: string; item_id: string; type: string; file_name: string; file_size?: number; file_path?: string; s3_key?: string; status: string; reject_reason?: string; text?: string; uploader: string; created_at: string }
interface Notification { id: string; type: string; recipients: string | string[]; channel: string; status: string; sent_at?: string; scheduled_at?: string; sender_name?: string }
interface ProjectData {
  id: string; name: string; status: string; project: string; biz_no?: string;
  ceo?: string; ceo_phone?: string; ceo_email?: string;
  manager?: string; manager_phone?: string; manager_email?: string;
  referrer?: string; ref_phone?: string; ref_email?: string;
  deadline?: string; created_by?: string; created_at?: string; company_id?: string;
  items: Item[]; uploads: Upload[]; projectGuides?: Guide[];
  notifications?: Notification[];
}

interface Props { projectId: string; onClose: () => void; onToast: (msg: string, type?: string) => void }

const G: Record<number, string> = { 50:'#F9FAFB',100:'#F3F4F6',200:'#E5E7EB',300:'#D1D5DB',400:'#9CA3AF',500:'#6B7280',600:'#4B5563',700:'#374151',800:'#1F2937',900:'#111827' };
const A = '#2E75B6', ALt = '#EFF6FF', OK = '#059669', OKLt = '#ECFDF5', W = '#D97706', WLt = '#FFF8E1', E = '#DC2626', ELt = '#FEF2F2', P = '#1B3A5C';
const TYPE_LABEL: Record<string,string> = { file:'📎 파일', text:'━ 단문', text_long:'≡ 장문', radio:'◉ 객관식', checkbox:'☑ 체크박스' };

function fmt(d?: string) { if (!d) return '-'; const dt = new Date(d); return `${dt.getFullYear()}.${String(dt.getMonth()+1).padStart(2,'0')}.${String(dt.getDate()).padStart(2,'0')} ${String(dt.getHours()).padStart(2,'0')}:${String(dt.getMinutes()).padStart(2,'0')}`; }
function fmtD(d?: string) { if (!d) return '-'; return d.slice(0,10).replace(/-/g,'.'); }
function fmtSize(s?: number) { if (!s) return ''; return s < 1024*1024 ? Math.round(s/1024)+'KB' : (s/(1024*1024)).toFixed(1)+'MB'; }

function Overlay({ onClose, children }: { onClose: () => void; children: React.ReactNode }) {
  return (
    <div style={{ position:'fixed',inset:0,zIndex:2000,display:'flex',alignItems:'center',justifyContent:'center' }}>
      <div style={{ position:'absolute',inset:0,background:'rgba(0,0,0,0.45)',backdropFilter:'blur(4px)' }} onClick={onClose} />
      {children}
    </div>
  );
}

function HdrBtn({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return <button onClick={onClick} style={{ padding:'7px 14px',borderRadius:8,border:'1px solid rgba(255,255,255,.2)',background:'rgba(255,255,255,.1)',color:'#fff',fontSize:12,fontWeight:500,cursor:'pointer' }}>{children}</button>;
}

function ActBtn({ bg, bc, color, disabled, onClick, children }: { bg:string;bc:string;color:string;disabled?:boolean;onClick:()=>void;children:React.ReactNode }) {
  return <button disabled={disabled} onClick={onClick} style={{ padding:'3px 8px',borderRadius:5,border:`1px solid ${bc}`,background:bg,color,fontSize:9,fontWeight:600,cursor:'pointer',whiteSpace:'nowrap' }}>{children}</button>;
}

/** 민감 항목 마스킹 (사용자 확정: 텍스트/파일명 ***) */
function maskFileName(name?: string){ const n=String(name||''); const i=n.lastIndexOf('.'); return i>0?'***'+n.slice(i):'***'; }
const MASK_TEXT = '*** (민감정보)';

export default function CollectDetailModal({ projectId, onClose, onToast }: Props) {
  const [project, setProject] = useState<ProjectData|null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [rejectTarget, setRejectTarget] = useState<{ids:string[];label:string}|null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [revealed, setRevealed] = useState<Record<string, boolean>>({}); // 민감 행별 공개
  const [confirmModal, setConfirmModal] = useState<{msg:string;onOk:()=>void}|null>(null);
  const [previewModal, setPreviewModal] = useState<{url:string;fileName:string;isImg:boolean;isPdf:boolean}|null>(null);
  const [pdfEditId, setPdfEditId] = useState<string|null>(null);
  const [copied, setCopied] = useState(false);
  const [bundleOpen, setBundleOpen] = useState(false);
  const [showAllNotifs, setShowAllNotifs] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.getCollectProject(projectId);
      // API 응답: {success, data: {...}} 또는 직접 객체
      const d = res?.data || res;
      setProject(d);
    } catch { onToast('프로젝트 조회 실패','error'); }
    setLoading(false);
  }, [projectId, onToast]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Overlay onClose={onClose}><div style={{ padding:40,color:G[400],textAlign:'center' }}>로딩 중...</div></Overlay>;
  if (!project) return null;

  const c = project;
  const items = c.items || [];
  const uploads = c.uploads || [];
  const isClosed = c.status === 'closed';
  const pendCount = uploads.filter(u => u.status==='pending').length;
  const reqItems = items.filter(i => i.required);
  const pct = items.length===0 ? 0 : reqItems.length===0 ? 0 : Math.round(reqItems.filter(i => {
    const app = uploads.filter(u => u.item_id===i.id && u.status==='approved');
    const rej = uploads.filter(u => u.item_id===i.id && u.status==='rejected');
    return app.length>0 && rej.length===0;
  }).length / reqItems.length * 100);

  const lk = (window.location.origin||'https://roum-partners.com')+'/portal/'+c.id;
  const shareLk = (window.location.origin||'https://roum-partners.com')+'/api/portal/'+c.id+'/share';

  function copyLink() { setBundleOpen(true); }

  async function doApprove(uploadId: string) {
    try { setActionLoading(true); await api.approveCollectFile(uploadId); await load(); onToast('승인 완료'); } catch { onToast('승인 실패','error'); } finally { setActionLoading(false); }
  }
  async function doUnapprove(uploadId: string) {
    try { setActionLoading(true); await api.unapproveCollectFile(uploadId); await load(); onToast('승인 취소됨'); } catch { onToast('오류','error'); } finally { setActionLoading(false); }
  }
  async function doBatchApprove(ids: string[]) {
    try { setActionLoading(true); await api.batchApproveCollect(ids); await load(); onToast(`${ids.length}건 승인 완료`); } catch { onToast('오류','error'); } finally { setActionLoading(false); }
  }
  async function doReject() {
    if (!rejectTarget || !rejectReason.trim()) return;
    try {
      setActionLoading(true);
      if (rejectTarget.ids.length===1) await api.rejectCollectFile(rejectTarget.ids[0], rejectReason);
      else await api.batchRejectCollect(rejectTarget.ids, rejectReason);
      setRejectTarget(null); setRejectReason(''); await load(); onToast('반려 완료');
    } catch { onToast('반려 실패','error'); } finally { setActionLoading(false); }
  }
  async function doDeleteUpload(uploadId: string) {
    try { await api.deleteCollectUpload(uploadId); await load(); onToast('삭제 완료'); } catch { onToast('삭제 실패','error'); }
  }
  async function doDownload(uploadId: string) {
    try { const data = await api.getCollectFileDownloadUrl(uploadId); if (data.url) window.open(data.url,'_blank'); } catch { onToast('다운로드 오류','error'); }
  }
  async function doDownloadAll() {
    try {
      const token = localStorage.getItem('token')||'';
      const resp = await fetch('/api/uploads/batch-download-zip', {
        method:'POST', headers:{'Content-Type':'application/json',Authorization:'Bearer '+token},
        body: JSON.stringify({ projectId }),
      });
      if (!resp.ok) { onToast('다운로드 실패','error'); return; }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url;
      const cd = resp.headers.get('content-disposition')||'';
      a.download = decodeURIComponent((cd.match(/filename="?([^"]+)"?/)||[])[1]||'download.zip');
      document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch { onToast('다운로드 오류','error'); }
  }
  async function doPreview(uploadId: string, fileName: string) {
    try {
      const data = await api.getCollectFileDownloadUrl(uploadId);
      if (!data.url) return;
      const isImg = /\.(jpg|jpeg|png|gif|webp)$/i.test(fileName);
      const isPdf = fileName.toLowerCase().endsWith('.pdf');
      if (isImg) { setPreviewModal({ url:data.url, fileName, isImg:true, isPdf:false }); }
      else if (isPdf) {
        const tk = localStorage.getItem('token')||'';
        const resp = await fetch(`/api/uploads/${uploadId}/pdf-proxy?token=${tk}`);
        const blob = await resp.blob();
        setPreviewModal({ url:URL.createObjectURL(blob), fileName, isImg:false, isPdf:true });
      } else { window.open(data.url,'_blank'); }
    } catch { onToast('미리보기 오류','error'); }
  }
  async function doSaveToStorage(u: Upload, item: Item) {
    if (!c.company_id) return;
    const token = localStorage.getItem('token')||'';
    try {
      await fetch(`/api/tm/companies/${c.company_id}/storage`, {
        method:'POST', headers:{'Content-Type':'application/json',Authorization:'Bearer '+token},
        body: JSON.stringify({ s3Key:u.file_path, fileName:u.file_name, source:c.project||'자료수집', itemTitle:item.label||'', sensitive:!!item.sensitive }),
      });
      onToast('저장소에 저장 완료');
    } catch { onToast('저장 실패','error'); }
  }
  async function doSaveTextToStorage(u: Upload, item: Item) {
    if (!c.company_id) return;
    if (!u.text || !u.text.trim()) { onToast('저장할 텍스트가 없습니다','error'); return; }
    const token = localStorage.getItem('token')||'';
    try {
      await fetch(`/api/tm/companies/${c.company_id}/storage/text`, {
        method:'POST', headers:{'Content-Type':'application/json',Authorization:'Bearer '+token},
        body: JSON.stringify({ text:u.text, itemTitle:item.label||'', source:c.project||'자료수집', sensitive:!!item.sensitive }),
      });
      onToast('저장소에 저장 완료');
    } catch { onToast('저장 실패','error'); }
  }
  function ask(msg: string, fn: () => void) { setConfirmModal({ msg, onOk: fn }); }

  const cardS: React.CSSProperties = { background:'#fff',borderRadius:12,border:`1px solid ${G[200]}`,padding:'14px 16px',marginBottom:10 };

  return (
    <Overlay onClose={onClose}>
      <div onClick={e=>e.stopPropagation()} style={{ position:'relative',width:'100%',maxWidth:860,height:'90vh',borderRadius:16,background:G[50],display:'flex',flexDirection:'column',overflow:'hidden',boxShadow:'0 20px 60px rgba(0,0,0,0.25)' }}>
        <div style={{ flex:1,overflowY:'auto',padding:'0 0 20px' }}>
          {/* 닫기 */}
          <div style={{ padding:'12px 20px 0',display:'flex',justifyContent:'flex-end' }}>
            <button onClick={onClose} style={{ padding:'4px 12px',borderRadius:6,border:`1px solid ${G[200]}`,background:'#fff',fontSize:12,color:G[500],cursor:'pointer' }}>✕ 닫기</button>
          </div>
          {isClosed && <div style={{ margin:'8px 20px 0',padding:'10px 14px',background:G[100],borderRadius:10,border:`1px solid ${G[300]}`,fontSize:12,color:G[600] }}>🔒 이 프로젝트는 종료 처리되었습니다.</div>}

          {/* 헤더 카드 */}
          <div style={{ margin:'10px 20px 0',background:`linear-gradient(135deg,${P},#264D73)`,padding:'22px 24px',borderRadius:16 }}>
            <div style={{ display:'flex',justifyContent:'space-between',alignItems:'flex-start' }}>
              <div>
                <div style={{ display:'flex',alignItems:'center',gap:8 }}>
                  <h1 style={{ color:'#fff',fontSize:20,fontWeight:700,margin:0 }}>{c.name}</h1>
                  {isClosed && <span style={{ fontSize:10,fontWeight:600,color:G[300],background:'rgba(255,255,255,.12)',padding:'2px 8px',borderRadius:5 }}>종료</span>}
                </div>
                {c.project && <p style={{ color:'rgba(255,255,255,.7)',fontSize:13,fontWeight:500,margin:'4px 0 0' }}>{c.project}</p>}
                <p style={{ color:'rgba(255,255,255,.5)',fontSize:12,margin:'4px 0 0' }}>
                  {c.biz_no && <span>{c.biz_no} · </span>}
                  {c.ceo && <span>{c.ceo}{c.manager ? ` 대표 · ${c.manager} 담당` : ' 대표'}</span>}
                  {c.referrer && <span> · {c.referrer}</span>}
                  {c.deadline && <span> · 기한: {fmtD(c.deadline)}</span>}
                </p>
                <p style={{ color:'rgba(255,255,255,.35)',fontSize:11,margin:'3px 0 0' }}>등록: {c.created_by||''} · {fmtD(c.created_at)}</p>
              </div>
              <div style={{ display:'flex',gap:6,alignItems:'center' }}>
                {pendCount>0 && <span style={{ background:'#F59E0B',color:'#fff',padding:'4px 10px',borderRadius:7,fontSize:12,fontWeight:700 }}>대기 {pendCount}</span>}
                <span style={{ background:pct===100?OK:'rgba(255,255,255,.15)',color:'#fff',padding:'5px 14px',borderRadius:8,fontSize:15,fontWeight:700 }}>{pct}%</span>
              </div>
            </div>
            <div style={{ display:'flex',gap:6,marginTop:14,flexWrap:'wrap' }}>
              <HdrBtn onClick={()=>window.open(lk,'_blank')}>👁 포털 미리보기</HdrBtn>
              <HdrBtn onClick={copyLink}>{copied?'✓ 복사됨':'📋 링크 복사'}</HdrBtn>
              <HdrBtn onClick={doDownloadAll}>⬇ 일괄 다운로드</HdrBtn>
            </div>
            <div style={{ marginTop:12,padding:'6px 12px',background:'rgba(255,255,255,.08)',borderRadius:8 }}>
              <span style={{ fontSize:12,color:isClosed?'rgba(255,255,255,.3)':'rgba(255,255,255,.5)',fontFamily:'monospace',wordBreak:'break-all',textDecoration:isClosed?'line-through':'none' }}>{lk}</span>
            </div>
          </div>

          {/* 참고자료 */}
          {c.projectGuides && c.projectGuides.length>0 && (
            <div style={{ margin:'10px 20px 0',...cardS,background:'#FFFBEB' }}>
              <span style={{ fontSize:13,fontWeight:600,color:'#92400E' }}>📎 참고자료</span>
              <div style={{ marginTop:6,display:'flex',flexWrap:'wrap',gap:6 }}>
                {c.projectGuides.map((g,i) => (
                  <span key={i} onClick={()=>{ if(g.type==='link'){let url=g.url||''; if(url&&!url.startsWith('http'))url='https://'+url; window.open(url,'_blank');} }}
                    style={{ display:'inline-flex',alignItems:'center',gap:3,fontSize:11,color:A,cursor:'pointer',padding:'2px 6px',background:'#FEF3C7',borderRadius:4 }}>
                    {g.type==='link'?'🔗':'📎'} {g.label||'참조'}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* 항목 목록 */}
          <div style={{ padding:'10px 20px 0' }}>
            {items.map((item) => {
              const iu = uploads.filter(u => u.item_id===item.id);
              const pending = iu.filter(u => u.status==='pending');
              const approved = iu.filter(u => u.status==='approved');
              let rejected = iu.filter(u => u.status==='rejected');
              if (approved.length>0) rejected = [];
              const visibleUploads = [...pending,...approved,...rejected];
              const done = approved.length>0 && pending.length===0;

              let badge = null;
              if (done) badge = <span style={{ fontSize:10,fontWeight:600,color:OK,background:OKLt,padding:'2px 8px',borderRadius:5 }}>✓ 완료</span>;
              else if (pending.length>0) badge = <span style={{ fontSize:10,fontWeight:600,color:W,background:WLt,padding:'2px 8px',borderRadius:5 }}>대기 {pending.length}</span>;
              else if (rejected.length>0) badge = <span style={{ fontSize:10,fontWeight:600,color:E,background:ELt,padding:'2px 8px',borderRadius:5 }}>반려</span>;

              return (
                <div key={item.id} style={{ ...cardS }}>
                  <div style={{ display:'flex',alignItems:'center',gap:8,marginBottom:visibleUploads.length>0?10:0 }}>
                    <span style={{ flex:1,fontSize:12,fontWeight:600,color:G[700] }}>{item.label}</span>
                    <span style={{ fontSize:10,color:G[400] }}>{TYPE_LABEL[item.type]||item.type}</span>
                    {item.required && <span style={{ fontSize:9,color:A,fontWeight:700 }}>필수</span>}
                    {item.sensitive && <span style={{ fontSize:9,color:'#D97706',fontWeight:700,background:'#FFFBEB',padding:'1px 5px',borderRadius:3 }}>민감</span>}
                    {badge}
                  </div>

                  {item.description && <div style={{ fontSize:11,color:G[500],marginBottom:6,whiteSpace:"pre-wrap" }}>{item.description}</div>}

                  {item.guides && item.guides.length>0 && (
                    <div style={{ display:'flex',flexWrap:'wrap',gap:6,marginBottom:8 }}>
                      {item.guides.map((g,gi) => (
                        <span key={gi} onClick={async()=>{
                          if(g.type==='link'){let url=g.url||'';if(url&&!url.startsWith('http'))url='https://'+url;window.open(url,'_blank');}
                          else if(g.type==='file'&&g.id){try{const data=await api.getCollectGuideDownloadUrl(projectId,g.id);if(data.url)window.open(data.url,'_blank');}catch{onToast('가이드 다운로드 실패','error');}}
                        }} style={{ display:'inline-flex',alignItems:'center',gap:3,fontSize:10,color:A,cursor:'pointer',padding:'2px 6px',background:ALt,borderRadius:4 }}>
                          {g.type==='link'?'🔗':'📎'} {g.label||g.fileName||g.file_name||'참조'}
                        </span>
                      ))}
                    </div>
                  )}

                  {visibleUploads.map(u => {
                    const stColor = u.status==='approved'?OK:u.status==='rejected'?E:W;
                    const stLabel = u.status==='approved'?'승인':u.status==='rejected'?'반려':'대기';
                    const isPdf = u.type==='file' && u.file_name?.toLowerCase().endsWith('.pdf');
                    const isFile = u.type==='file';
                    return (
                      <div key={u.id} style={{ display:'flex',alignItems:'center',gap:8,padding:'8px 10px',background:u.status==='pending'?WLt:u.status==='rejected'?ELt:OKLt,borderRadius:8,marginBottom:4,flexWrap:'wrap' }}>
                        <span style={{ fontSize:14 }}>{isFile?'📄':'📝'}</span>
                        <div style={{ flex:1,minWidth:0 }}>
                          {item.sensitive && !revealed[u.id] ? (
                            <span style={{ fontSize:12,color:G[400],fontWeight:500,letterSpacing:1 }}>{isFile?maskFileName(u.file_name):MASK_TEXT}</span>
                          ) : isFile ? (
                            <span onClick={()=>doPreview(u.id,u.file_name)} style={{ fontSize:12,color:A,fontWeight:500,cursor:'pointer',textDecoration:'underline' }}>{u.file_name}</span>
                          ) : (
                            <span style={{ fontSize:12,color:G[700] }}>{u.text||''}</span>
                          )}
                          {item.sensitive && <button onClick={()=>setRevealed(p=>({...p,[u.id]:!p[u.id]}))} title={revealed[u.id]?'다시 가리기':'민감정보 보기'} style={{ marginLeft:6,padding:'1px 6px',borderRadius:5,border:'1px solid '+G[200],background:'#fff',fontSize:10,cursor:'pointer',color:G[500] }}>{revealed[u.id]?'🙈':'👁'}</button>}
                          {isFile && u.file_size ? <span style={{ fontSize:10,color:G[400],marginLeft:6 }}>({fmtSize(u.file_size)})</span> : null}
                          <span style={{ fontSize:10,color:G[400],marginLeft:6 }}>{u.uploader} · {fmt(u.created_at)}</span>
                        </div>
                        <span style={{ fontSize:10,fontWeight:600,color:stColor }}>{stLabel}</span>
                        {u.status==='pending' && <>
                          <ActBtn bg={OKLt} bc={OK} color={OK} disabled={actionLoading} onClick={()=>doApprove(u.id)}>승인</ActBtn>
                          <ActBtn bg={ELt} bc={E} color={E} disabled={actionLoading} onClick={()=>{setRejectTarget({ids:[u.id],label:item.label});setRejectReason('');}}>반려</ActBtn>
                        </>}
                        {u.status==='approved' && <ActBtn bg={WLt} bc={W} color={W} onClick={()=>ask('승인을 취소하시겠습니까?',()=>doUnapprove(u.id))}>승인취소</ActBtn>}
                        {isPdf && <ActBtn bg={ALt} bc={A} color={A} onClick={()=>setPdfEditId(u.id)}>PDF 편집</ActBtn>}
                        {isFile && c.company_id && <button onClick={()=>doSaveToStorage(u,item)} title="서버 저장" style={{ padding:'4px 8px',borderRadius:6,border:'1px solid #059669',background:'#F0FDF4',fontSize:10,cursor:'pointer' }}>💾</button>}
                        {!isFile && c.company_id && u.text && <button onClick={()=>doSaveTextToStorage(u,item)} title="텍스트 저장공간 저장" style={{ padding:'4px 8px',borderRadius:6,border:'1px solid #059669',background:'#F0FDF4',fontSize:10,cursor:'pointer' }}>💾</button>}
                        {isFile && <button onClick={()=>doDownload(u.id)} style={{ padding:'4px 8px',borderRadius:6,border:`1px solid ${G[200]}`,background:'#fff',fontSize:10,cursor:'pointer' }}>⬇</button>}
                        <button onClick={()=>ask('이 제출물을 삭제하시겠습니까?',()=>doDeleteUpload(u.id))} style={{ background:'none',border:'none',cursor:'pointer',fontSize:12,color:G[400] }}>🗑</button>
                      </div>
                    );
                  })}

                  {pending.length>=2 && (
                    <div style={{ display:'flex',gap:6,marginTop:6 }}>
                      <button disabled={actionLoading} onClick={()=>doBatchApprove(pending.map(u=>u.id))} style={{ padding:'5px 12px',borderRadius:6,border:'none',background:OK,color:'#fff',fontSize:11,fontWeight:600,cursor:'pointer' }}>일괄 승인 ({pending.length}건)</button>
                      <button disabled={actionLoading} onClick={()=>{setRejectTarget({ids:pending.map(u=>u.id),label:`${item.label} (${pending.length}건)`});setRejectReason('');}} style={{ padding:'5px 12px',borderRadius:6,border:'none',background:E,color:'#fff',fontSize:11,fontWeight:600,cursor:'pointer' }}>일괄 반려</button>
                    </div>
                  )}

                  {rejected.length>0 && (()=>{
                    const reasons: string[] = [];
                    rejected.forEach(u=>{if(u.reject_reason&&!reasons.includes(u.reject_reason))reasons.push(u.reject_reason);});
                    return reasons.map((reason,ri)=>(
                      <div key={ri} style={{ marginTop:4,padding:'6px 10px',background:ELt,border:'1px solid #FECACA',borderRadius:6,fontSize:11,color:E }}>반려 사유: {reason}</div>
                    ));
                  })()}

                  {visibleUploads.length===0 && !item.description && (
                    <div style={{ fontSize:11,color:G[400],textAlign:'center',padding:'8px 0' }}>아직 제출된 파일이 없습니다</div>
                  )}
                </div>
              );
            })}
          </div>

          {/* 알림 이력 */}
          {c.notifications && c.notifications.length>0 && (
            <div style={{ margin:'0 20px',...cardS }}>
              <div style={{ fontSize:13,fontWeight:600,color:G[900],marginBottom:10,display:'flex',justifyContent:'space-between' }}>
                <span>알림 발송 이력</span>
                <span style={{ fontSize:11,color:G[400] }}>{c.notifications.length}건</span>
              </div>
              {(showAllNotifs?c.notifications:c.notifications.slice(0,3)).map(n=>{
                const types: Record<string,string> = {initial:'최초 안내',reminder:'리마인더',reject:'반려 안내',combo:'미제출+반려',item_change:'항목 변경',miss_change:'미제출+변경'};
                let recips: string[] = [];
                try { recips = Array.isArray(n.recipients)?n.recipients:JSON.parse(n.recipients||'[]'); } catch { recips=[String(n.recipients)]; }
                const ch = n.channel==='sms'?'SMS':n.channel==='email'?'Email':'SMS+Email';
                return (
                  <div key={n.id} style={{ padding:'10px 12px',borderBottom:`1px solid ${G[100]}`,fontSize:12 }}>
                    <div style={{ display:'flex',alignItems:'center',gap:6,marginBottom:4 }}>
                      <span style={{ color:A,fontWeight:600 }}>{types[n.type]||n.type}</span>
                      <span style={{ fontSize:10,fontWeight:600,color:n.status==='sent'?OK:W }}>{n.status==='sent'?'발송완료':n.status==='scheduled'?'예약':''}</span>
                    </div>
                    <div style={{ fontSize:11,color:G[600],display:'flex',flexWrap:'wrap',gap:12 }}>
                      <span>받는 사람 - {recips.join(', ')||'-'}</span>
                      <span>채널 - {ch}</span>
                      <span>발송 - {fmt(n.sent_at||n.scheduled_at)}</span>
                    </div>
                  </div>
                );
              })}
              {!showAllNotifs && c.notifications.length>3 && <button onClick={()=>setShowAllNotifs(true)} style={{ width:'100%',padding:8,border:`1px solid ${G[200]}`,borderRadius:8,background:G[50],color:G[500],fontSize:12,cursor:'pointer',marginTop:6 }}>더보기 ({c.notifications.length-3}건)</button>}
              {showAllNotifs && c.notifications.length>3 && <button onClick={()=>setShowAllNotifs(false)} style={{ width:'100%',padding:8,border:`1px solid ${G[200]}`,borderRadius:8,background:G[50],color:G[500],fontSize:12,cursor:'pointer',marginTop:6 }}>접기</button>}
            </div>
          )}
        </div>
      </div>

      {/* 반려 사유 모달 */}
      {rejectTarget && (
        <div style={{ position:'fixed',inset:0,zIndex:2100,display:'flex',alignItems:'center',justifyContent:'center' }}>
          <div style={{ position:'absolute',inset:0,background:'rgba(0,0,0,0.3)' }} onClick={()=>setRejectTarget(null)} />
          <div style={{ position:'relative',width:380,borderRadius:12,background:'#fff',padding:20,boxShadow:'0 4px 20px rgba(0,0,0,0.15)' }}>
            <div style={{ fontSize:14,fontWeight:700,marginBottom:6 }}>반려 사유</div>
            <div style={{ fontSize:12,color:G[500],marginBottom:10 }}>{rejectTarget.label}</div>
            <textarea value={rejectReason} onChange={e=>setRejectReason(e.target.value)} rows={3} placeholder="반려 사유를 입력하세요"
              style={{ width:'100%',padding:'8px 10px',borderRadius:8,border:`1px solid ${G[200]}`,fontSize:13,outline:'none',resize:'vertical',boxSizing:'border-box' }} />
            <div style={{ display:'flex',gap:8,marginTop:12 }}>
              <button onClick={()=>setRejectTarget(null)} style={{ flex:1,padding:'10px',borderRadius:8,border:`1px solid ${G[200]}`,background:'#fff',fontSize:13,cursor:'pointer' }}>취소</button>
              <button onClick={doReject} disabled={!rejectReason.trim()} style={{ flex:1,padding:'10px',borderRadius:8,border:'none',background:E,color:'#fff',fontSize:13,fontWeight:600,cursor:'pointer',opacity:rejectReason.trim()?1:0.5 }}>반려</button>
            </div>
          </div>
        </div>
      )}

      {/* 확인 모달 */}
      {confirmModal && (
        <div style={{ position:'fixed',inset:0,zIndex:2100,display:'flex',alignItems:'center',justifyContent:'center' }}>
          <div style={{ position:'absolute',inset:0,background:'rgba(0,0,0,0.3)' }} onClick={()=>setConfirmModal(null)} />
          <div style={{ position:'relative',width:360,borderRadius:12,background:'#fff',padding:20,boxShadow:'0 4px 20px rgba(0,0,0,0.15)' }}>
            <div style={{ fontSize:13,color:G[700],whiteSpace:'pre-wrap',marginBottom:16 }}>{confirmModal.msg}</div>
            <div style={{ display:'flex',gap:8,justifyContent:'flex-end' }}>
              <button onClick={()=>setConfirmModal(null)} style={{ padding:'8px 16px',borderRadius:8,border:`1px solid ${G[200]}`,background:'#fff',fontSize:13,cursor:'pointer' }}>취소</button>
              <button onClick={()=>{confirmModal.onOk();setConfirmModal(null);}} style={{ padding:'8px 16px',borderRadius:8,border:'none',background:A,color:'#fff',fontSize:13,fontWeight:600,cursor:'pointer' }}>확인</button>
            </div>
          </div>
        </div>
      )}

      {/* 미리보기 모달 */}
      {previewModal && (
        <div style={{ position:'fixed',inset:0,zIndex:2200,background:'rgba(0,0,0,.7)',display:'flex',alignItems:'center',justifyContent:'center' }} onClick={()=>setPreviewModal(null)}>
          <div style={{ background:'#fff',borderRadius:12,maxWidth:'90vw',maxHeight:'90vh',overflow:'hidden',display:'flex',flexDirection:'column' }} onClick={e=>e.stopPropagation()}>
            <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',padding:'10px 16px',borderBottom:`1px solid ${G[200]}` }}>
              <span style={{ fontSize:13,fontWeight:600,color:G[700] }}>{previewModal.fileName}</span>
              <button onClick={()=>setPreviewModal(null)} style={{ background:'none',border:'none',cursor:'pointer',fontSize:16,color:G[400] }}>✕</button>
            </div>
            <div style={{ overflow:'auto',maxHeight:'80vh',padding:8 }}>
              {previewModal.isImg && <img src={previewModal.url} alt="" style={{ maxWidth:'80vw',maxHeight:'75vh',objectFit:'contain' }} />}
              {previewModal.isPdf && <iframe src={previewModal.url} title="PDF" style={{ width:'80vw',height:'75vh',border:'none' }} />}
            </div>
          </div>
        </div>
      )}

      {/* PDF 편집기 */}
      {pdfEditId && <PdfEditor uploadId={pdfEditId} onClose={()=>setPdfEditId(null)} onSaved={()=>load()} />}
      {bundleOpen && <BundleLinkModal projectId={c.id || projectId} onClose={() => setBundleOpen(false)} onToast={onToast} />}
    </Overlay>
  );
}
